<template>
  <div>
    <v-alert
      v-if="showError"
      border="start"
      :close-text="t('generic.labels.close')"
      colored-border
      elevation="2"
      type="warning"
    >
      {{ t('donorDetails.pidRetrievalError') }}
    </v-alert>
    <v-slide-x-transition>
      <v-text-field
        v-if="!manualInput"
        ref="ssn"
        v-model="donor.socialSecurityNumber"
        v-mask="ssnMask"
        :disabled="ssnLoading"
        :hint="t('donorDetails.form.pid.hint')"
        :label="t('donorDetails.form.pid.label')"
        :loading="ssnLoading"
        :persistent-hint="!showInputFields"
        :placeholder="t('donorDetails.form.pid.placeholder')"
        :rules="personalIdentityFieldRules"
        :success="showInputFields"
        data-cy="donor-form-ssn-field"
        :required="!allowManualPersonalIdentityNumberInput"
        validate-on-blur
      />
    </v-slide-x-transition>
    <!-- Manual input switch for ssn -->
    <v-switch
      v-if="allowManualPersonalIdentityNumberInput"
      v-model="manualInput"
      :label="t('donorDetails.manualInput')"
      color="primary"
      data-cy="donor-form-manual-input-switch"
    />
    <v-slide-y-transition>
      <div v-show="showInputFields || manualInput">
        <v-text-field
          ref="email"
          v-model="donorEmail"
          :hint="t('donorDetails.form.email.hint')"
          :label="t('donorDetails.form.email.label') + '*'"
          :persistent-hint="true"
          :placeholder="t('donorDetails.form.email.placeholder')"
          :rules="[rules.email, rules.required]"
          :validate-on-blur="true"
          class="mb-6 mt-0"
          data-cy="donor-form-email-field"
          prepend-inner-icon="mdi-email-outline"
          type="email"
          @keydown.space.prevent
        />
        <v-divider class="mt-4 mb-4" />
        <v-text-field
          v-model="donor.firstname"
          :disabled="!manualInput"
          :hint="t('donorDetails.form.firstname.hint')"
          :label="t('donorDetails.form.firstname.label') + '*'"
          :placeholder="t('donorDetails.form.firstname.placeholder')"
          :rules="personalInformationInputFieldRules"
          data-cy="donor-form-firstname-field"
          :required="mandatoryPersonalInformationInputFields"
        />
        <v-text-field
          v-model="donor.surname"
          :disabled="!manualInput"
          :hint="t('donorDetails.form.lastname.hint')"
          :label="t('donorDetails.form.lastname.label') + '*'"
          :placeholder="t('donorDetails.form.lastname.placeholder')"
          :rules="personalInformationInputFieldRules"
          data-cy="donor-form-surname-field"
          :required="mandatoryPersonalInformationInputFields"
        />
        <v-text-field
          v-model="donor.address.address1"
          :disabled="!manualInput"
          :hint="t('donorDetails.form.address.hint')"
          :label="t('donorDetails.form.address.label') + '*'"
          :placeholder="t('donorDetails.form.address.placeholder')"
          :rules="personalInformationInputFieldRules"
          data-cy="donor-form-address1-field"
        />
        <v-text-field
          v-model="donor.address.postcode"
          :disabled="!manualInput"
          :hint="t('donorDetails.form.postcode.hint')"
          :label="t('donorDetails.form.postcode.label') + '*'"
          :placeholder="t('donorDetails.form.postcode.placeholder')"
          :rules="personalInformationInputFieldRules"
          data-cy="donor-form-postcode-field"
        />
        <v-text-field
          v-model="donor.address.city"
          :disabled="!manualInput"
          :hint="t('donorDetails.form.city.hint')"
          :label="t('donorDetails.form.city.label') + '*'"
          :placeholder="t('donorDetails.form.city.placeholder')"
          :rules="personalInformationInputFieldRules"
          data-cy="donor-form-city-field"
        />
        <v-text-field
          v-model="donor.address.country"
          :disabled="!manualInput"
          :hint="t('donorDetails.form.country.hint')"
          :label="t('donorDetails.form.country.label') + '*'"
          :placeholder="t('donorDetails.form.country.placeholder')"
          :rules="personalInformationInputFieldRules"
          data-cy="donor-form-country-field"
        />
        <confirm-field-component
          v-if="showPep"
          id="pepConfirmComponent"
          :value="pep"
          :required="mandatoryPersonalInformationInputFields"
          :rules="[rules.pep]"
          :label="t('donorDetails.form.pep.label')"
          :tooltip="t('donorDetails.form.pep.tooltip')"
        />
      </div>
    </v-slide-y-transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick } from 'vue';
import store from '@/store';
import { ssnPattern } from '@/validators';
import { Donor } from '@/interfaces/donation/donor';
import { mask } from 'vue-the-mask';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { getFromSpar } from '@/api/mainApi';
import { RequestSession } from '@/interfaces/requestSession';
import ConfirmFieldComponent from '@/components/form/ConfirmFieldComponent.vue';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { useValidationRules } from '@/composables/useValidationRules';
import { useLabel } from '@/composables/useLabel';

export default defineComponent({
  components: {
    ConfirmFieldComponent,
  },
  directives: {
    mask,
  },
  props: {
    modelValue: {
      type: Object as () => Donor,
      default: () => ({}) as Donor,
    },
    manual: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    pep: {
      type: Boolean,
      default: null,
    },
  },
  emits: ['update:modelValue', 'update:error', 'update:manual'],
  setup(props, { emit }) {
    // Data
    const ssnLoading = ref(false);
    const ssnMask = ref('########-####');
    const ssnValid = ref(false);
    const showInputFields = ref(false);

    const { rules } = useValidationRules();
    const { t } = useLabel();

    // From StepperFlowMixin
    const getDonationStepFlowVersion = (): DonationGuideVersion => {
      return store.state.donationGuideVersion || DonationGuideVersion.V1;
    };

    // Computed
    const steps = computed((): typeof stepV1 | typeof stepV2 => {
      return getDonationStepFlowVersion() == DonationGuideVersion.V1 ? stepV1 : stepV2;
    });
    const socialSecurityNumber = computed(() => {
      return donor.value.socialSecurityNumber;
    });
    const currentStep = computed(() => store.state.currentStep);
    const requestSession = computed((): RequestSession => store.state.requestSession!);
    const isDirectPaymentAvailable = computed(() => {
      return (
        requestSession.value.organizationGroup &&
        requestSession.value.organizationGroup.swedbankPaySettings &&
        requestSession.value.organizationGroup.swedbankPaySettings.id
      );
    });
    const optionalDonorPersonalIdentityNumber = computed((): boolean => {
      return !!requestSession.value?.optionalDonorPersonalIdentityNumber;
    });
    const allowManualPersonalIdentityNumberInput = computed((): boolean => {
      return !!(isDirectPaymentAvailable.value || optionalDonorPersonalIdentityNumber.value);
    });
    const mandatoryPersonalInformationInputFields = computed((): boolean => {
      return !optionalDonorPersonalIdentityNumber.value;
    });
    const personalIdentityFieldRules = computed(() => {
      return optionalDonorPersonalIdentityNumber.value ? [rules.value.pidEmpty] : [rules.value.pid];
    });
    const personalInformationInputFieldRules = computed(() => {
      return mandatoryPersonalInformationInputFields.value ? [rules.value.required] : [];
    });
    const showPep = computed(() => {
      // 2023-01-03 | PEP is no longer required so will always be hidden
      return false;
    });
    const donor = computed({
      get(): Donor {
        return props.modelValue as Donor;
      },
      set(val: Donor) {
        emit('update:modelValue', val);
      },
    });
    const showError = computed({
      get(): boolean {
        return props.error && !manualInput.value;
      },
      set(val: boolean) {
        emit('update:error', val);
      },
    });
    const manualInput = computed({
      get(): boolean {
        return props.manual;
      },
      set(val: boolean) {
        emit('update:manual', val);
      },
    });
    const donorEmail = computed({
      get(): string {
        return donor.value.email;
      },
      set(newValue: string) {
        donor.value.email = newValue;
      },
    });

    // Watch
    watch(currentStep, (val) => {
      const targetStep =
        getDonationStepFlowVersion() === DonationGuideVersion.V1
          ? stepV1.DONOR
          : stepV2.CERTIFICATE_DETAILS;
      if (val == targetStep && !showInputFields.value && !manualInput.value) {
        // Focus ssn ref
        nextTick(() => {
          // Assuming ref is defined
        });
      }
    });
    watch(manualInput, (val) => {
      if (!!val) {
        // if manualInput is toggled we empty all the fields
        donor.value = {
          address: {
            address1: '',
            address2: '',
            address3: '',
            city: '',
            country: '',
            postcode: '',
          },
          email: '',
          firstname: '',
          surname: '',
          socialSecurityNumber: '',
          token: '',
        } as Donor;
      }
    });
    watch(socialSecurityNumber, async (newValue, oldValue) => {
      const newValueExclHypen = newValue.replace(/-/g, '');
      const oldValueExclHypen = oldValue.replace(/-/g, '');
      if (newValueExclHypen !== oldValueExclHypen || showError.value) {
        showError.value = false;

        if (ssnPattern.test(newValue)) {
          ssnLoading.value = true;

          const formattedSsn: string = newValue.replace('-', '');
          const targetStep =
            getDonationStepFlowVersion() === DonationGuideVersion.V1
              ? stepV1.DONOR
              : stepV2.CERTIFICATE_DETAILS;
          if (currentStep.value == targetStep) {
            getFromSpar(formattedSsn)
              .then((data) => {
                data.socialSecurityNumber = newValue;
                donor.value = data;

                if (
                  !isDirectPaymentAvailable.value &&
                  data.givenName &&
                  data.givenName.length > 0
                ) {
                  nextTick(() => {
                    // must be nextTick so we update data after its been assigned
                    donor.value.firstname = data.givenName;
                  });
                }

                showInputFields.value = true;
                // Focus email ref
                nextTick(() => {
                  // Assuming ref is defined
                });
              })
              .catch(() => {
                showError.value = true;
              })
              .finally(() => {
                ssnLoading.value = false;
              });
          }
        } else if (showInputFields.value) {
          showInputFields.value = false;
        }
      }
    });

    return {
      ssnLoading,
      ssnMask,
      ssnValid,
      showInputFields,
      rules,
      t,
      steps,
      socialSecurityNumber,
      currentStep,
      requestSession,
      isDirectPaymentAvailable,
      optionalDonorPersonalIdentityNumber,
      allowManualPersonalIdentityNumberInput,
      mandatoryPersonalInformationInputFields,
      personalIdentityFieldRules,
      personalInformationInputFieldRules,
      showPep,
      donor,
      showError,
      manualInput,
      donorEmail,
    };
  },
});
</script>
